// Séance en direct : le coach suit son plan au chrono (D-019).
// Plein écran, décompte calé sur l'heure réelle (domain/session.ts), bip et
// vibration à la fin d'un exercice, écran maintenu allumé, récap à la fin.
import { presentOnly, readGroups } from '../domain/groups';
import { groupsDetail } from './exo-groups';
import { addMinutes, next, offsetMinutes, pause, projectedEnd, remainingMs, resume, startLive, type Live, type PlanItem } from '../domain/session';
import { $, html, mount } from './dom';
import { time } from './format';
import type { Team } from '../lib/api';
import type { Drill } from '../lib/drills-api';
import { bibsOf, drillLine, planDrills } from './drills';
import { launchDrill } from './drill-run';
import { openChrono } from './chrono';
import { drillThumb } from './drill-view';
import { liveGameButton, startGameFromItem } from './games';

const mmss = (ms: number) => {
  const s = Math.round(Math.abs(ms) / 1000);
  return `${ms < 0 ? '+' : ''}${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

function beep() {
  try {
    const ctx = new AudioContext();
    for (const [t, f] of [[0, 880], [0.25, 880], [0.5, 1320]] as const) {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.25, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.2);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.22);
    }
  } catch { /* pas de son : la vibration et l'écran suffisent */ }
  navigator.vibrate?.([200, 100, 200]);
}

export function openLive(items: PlanItem[], title: string, nameOf?: (id: string) => string, present?: string[], team?: Team | null): void {
  if (!items.length) return;
  // Une séance en direct cachée pendant qu'on préparait un jeu (D-162) : la nouvelle la remplace.
  document.querySelectorAll('.live[hidden]').forEach(x => x.dispatchEvent(new Event('live-close')));
  // Le drill de chaque exercice (D-098) : « ▶ Lancer le drill » ; à la fin du drill, on revient ici.
  let drillOf = new Map<string, Drill>();
  void planDrills(items).then(m => { drillOf = m; if (!l.done) draw(); }).catch(() => undefined);
  const host = document.createElement('div');
  host.className = 'live';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-label', 'Séance en direct');
  document.body.append(host);
  host.addEventListener('live-close', () => close());

  let l: Live = startLive(items, Date.now());
  let stopArmed = false;                          // « Arrêter ? » survit au rafraîchissement
  let rang = -1;                                   // exercice pour lequel le bip a déjà sonné
  let lock: { release(): Promise<void> } | null = null;
  const wake = async () => { try { lock = await (navigator as any).wakeLock?.request('screen'); } catch { lock = null; } };
  void wake();
  const onVis = () => { if (document.visibilityState === 'visible') { void wake(); draw(); } };
  document.addEventListener('visibilitychange', onVis);

  const close = () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVis);
    void lock?.release().catch(() => undefined);
    host.remove();
  };

  const draw = () => {
    const now = Date.now();
    if (l.done) {
      const planned = items.reduce((n, i) => n + i.minutes, 0);
      const actual = Math.round(l.log.reduce((n, x) => n + x.actualMs, 0) / 60000);
      mount(host, html`
        <div class="live-in">
          <p class="live-eyebrow">${title}</p>
          <h2 class="live-title">Séance terminée</h2>
          <p class="live-sub">${actual} min réelles pour ${planned} prévues</p>
          <ol class="live-recap">${l.log.map(x => html`<li><span>${items[x.index]!.title}</span>
            <span>${x.skipped ? 'passé' : `${Math.round(x.actualMs / 60000)} / ${items[x.index]!.minutes} min`}</span></li>`)}</ol>
          <button type="button" class="btn btn-primary" data-act="close">Fermer</button>
        </div>`);
      $(host, '[data-act=close]')!.addEventListener('click', close);
      return;
    }
    const cur = items[l.index]!, nxt = items[l.index + 1];
    const left = remainingMs(l, now);
    if (left <= 0 && rang !== l.index && l.pausedAt === null) { rang = l.index; beep(); }
    const off = offsetMinutes(l, items, now);
    mount(host, html`
      <div class="live-in ${left <= 0 ? 'live-over' : ''}">
        <div class="live-top">
          <span class="live-eyebrow">${l.index + 1} / ${items.length} · fin prévue ${time(new Date(projectedEnd(l, items, now)))}${off ? ` (${off > 0 ? '+' : ''}${off} min)` : ''}</span>
          <button type="button" class="live-x ${stopArmed ? 'armed' : ''}" data-act="stop" aria-label="Arrêter la séance">${stopArmed ? 'Arrêter ?' : '×'}</button>
        </div>
        <h2 class="live-title">${cur.title}</h2>
        ${cur.description ? html`<p class="live-sub">${cur.description}</p>` : ''}
        ${nameOf && readGroups(cur.groups).length ? groupsDetail(present ? presentOnly(readGroups(cur.groups), present) : readGroups(cur.groups), nameOf) : ''}
        <div class="live-clock" aria-live="polite">${left <= 0 ? 'Temps écoulé' : mmss(left)}</div>
        <p class="live-sub">${left <= 0 ? `dépassé de ${mmss(-left).replace('+', '')}` : `jusqu'à ${time(new Date(l.curEndAt + (l.pausedAt !== null ? now - l.pausedAt : 0)))}`}${l.pausedAt !== null ? ' · en pause' : ''}</p>
        <div class="dr-livedrill ${drillOf.get(cur.id) ? '' : 'solo'}">${drillOf.get(cur.id) ? html`<button type="button" class="d" data-act="drill">${drillThumb(drillOf.get(cur.id)!)}<span class="t"><strong>▶ Lancer le drill</strong><small>${drillLine(drillOf.get(cur.id)!)}</small></span></button>` : ''}
          <button type="button" class="c" data-act="chrono"><span class="ti ti-chrono" aria-hidden="true"></span>Chrono</button></div>
        ${team ? liveGameButton(cur) : ''}
        <div class="live-actions">
          ${l.pausedAt !== null
            ? html`<button type="button" class="btn btn-ghost" data-act="resume">Reprendre</button>`
            : html`<button type="button" class="btn btn-ghost" data-act="pause">Pause</button>`}
          <button type="button" class="btn btn-ghost" data-act="plus">+2 min</button>
          <button type="button" class="btn btn-primary" data-act="next">${nxt ? (left <= 0 ? 'Exercice suivant' : 'Passer') : 'Terminer'}</button>
        </div>
        ${nxt ? html`<p class="live-next">Ensuite : <strong>${nxt.title}</strong> · ${nxt.minutes} min</p>` : ''}
      </div>`);
    const act = (a: string, fn: () => void) => host.querySelector(`[data-act=${a}]`)?.addEventListener('click', () => { fn(); draw(); });
    act('pause', () => { l = pause(l, Date.now()); });
    act('resume', () => { l = resume(l, Date.now()); });
    act('plus', () => { l = addMinutes(l, 2, Date.now()); rang = -1; });
    act('next', () => { const n = Date.now(); l = next(l, items, n, remainingMs(l, n) > 0); });
    act('drill', () => { const d = drillOf.get(cur.id); if (d) void launchDrill(d, { team: team ?? null, staff: true }); });
    act('chrono', () => void openChrono({ team: team ?? null, bibs: bibsOf(cur) }));
    // « En faire un jeu » (D-162, P1) : la séance se cache le temps de préparer et de jouer, puis revient (elle a continué).
    act('jeu', () => { if (team) startGameFromItem(team, cur, present ?? null, 'live'); });
    const stop = host.querySelector<HTMLButtonElement>('[data-act=stop]')!;
    stop.addEventListener('click', () => {
      if (!stopArmed) { stopArmed = true; draw(); setTimeout(() => { stopArmed = false; }, 4000); return; }
      close();
    });
  };

  const timer = setInterval(draw, 1000);
  draw();
}
